import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID, inject } from '@angular/core';
import { Injectable } from '@angular/core';

import { EAnalyticsEvent, validateAnalyticsEvent } from '@common/analytics-catalog';

import { ITracker } from '../tracker';
import { EventQueueService } from './event-queue.service';
import { GOAL_TO_CATALOG, IGoalMapping } from './goal-catalog.map';
import { VisitorIdentityService } from './visitor-identity.service';

/**
 * Первопартийный трекер: адаптер {@link ITracker}, встающий в общий fan-out
 * `AnalyticsService` рядом с вендорными. `bootstrap` открывает сессию и шлёт
 * `session_start`; `pageView` — `page_view`; `event` мапит вендор-нейтральную цель в
 * событие каталога (`GOAL_TO_CATALOG`) с приведением params к его схеме, а неизвестную
 * цель пробует как прямое имя события. Невалидные каталогом — молча отбрасываются, в
 * поток попадают только события каталога. `setUser` — no-op: первопартийная идентичность
 * не связывается с внешним пользователем (privacy). Дедуп `session_start` — по новизне сессии.
 */
@Injectable({ providedIn: 'root' })
export class FirstPartyTracker implements ITracker {
    readonly #isBrowser: boolean = isPlatformBrowser(inject(PLATFORM_ID));
    readonly #identity: VisitorIdentityService = inject(VisitorIdentityService);
    readonly #queue: EventQueueService = inject(EventQueueService);

    public bootstrap(): void {
        if (!this.#isBrowser) {
            return;
        }
        this.#identity.visitorId();
        if (this.#identity.ensureSession().isNew) {
            this.#enqueue(EAnalyticsEvent.SessionStart, this.#sessionStartParams());
        }
    }

    public pageView(url: string, title?: string): void {
        this.#enqueue(EAnalyticsEvent.PageView, title ? { page_path: url, page_title: title } : { page_path: url });
    }

    public event(name: string, params?: Record<string, unknown>): void {
        const mapping: IGoalMapping | undefined = GOAL_TO_CATALOG[name];
        if (mapping) {
            this.#enqueue(mapping.event, mapping.toParams(params));
            return;
        }
        // Не в карте целей — возможно, уже имя события каталога; валидатор отсеет чужое.
        this.#enqueue(name, params);
    }

    public setUser(): void {
        // no-op: первопартийный visitor_id не связывается с внешним user_id.
    }

    #enqueue(name: string, params?: Record<string, unknown>): void {
        if (!this.#isBrowser) {
            return;
        }
        const verdict: ReturnType<typeof validateAnalyticsEvent> = validateAnalyticsEvent({ name, params });
        if (!verdict.ok) {
            return;
        }
        this.#queue.enqueue({
            name,
            params,
            occurredAt: new Date().toISOString(),
            pagePath: globalThis.location.pathname,
        });
    }

    #sessionStartParams(): Record<string, unknown> {
        const landingPath: string = globalThis.location.pathname;
        const referrer: string = globalThis.document.referrer;
        // Литерал целиком (без динамического присваивания) — иначе TS4111 на index-signature.
        return referrer ? { landing_path: landingPath, referrer } : { landing_path: landingPath };
    }
}
